import axios from 'axios';

export interface PrebookingData {
    item: string;
    dentistAndPractice: string;
    patientName: string;
    sendDate: string;
    returnDate: string;
    comments: string;
    arch?: string;
    teeth?: string;
    referenceId?: string;
}

// SVG Icons (same as lms-svelte)
export const icons = {
    label: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>`,
    workTicket: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>`,
} as const;

// Generate PDF from HTML content via Netlify function (to avoid CORS)
export async function generatePDF(
    htmlContent: string,
    dimensions: { width: string; height: string } = { width: '297mm', height: '210mm' }
): Promise<Blob> {
    const response = await axios.post(
        '/.netlify/functions/generate-pdf',
        {
            html: htmlContent,
            width: dimensions.width,
            height: dimensions.height,
        },
        {
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    // Convert base64 response back to blob
    const pdfBase64 = response.data.pdf;
    const binaryString = atob(pdfBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: 'application/pdf' });
}

// Download PDF blob
export function downloadPDF(pdfBlob: Blob, filename: string): void {
    const url = URL.createObjectURL(pdfBlob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// View HTML in new tab (for Option+click view mode - no PDF generation needed)
export function viewWorkTicketHTML(data: PrebookingData): void {
    const html = generateWorkTicketHTML(data);
    const w = window.open();
    if (w) {
        w.document.write(html);
        w.document.close();
    }
}

export function viewLabelHTML(data: PrebookingData): void {
    const html = generateLabelHTML(data);
    const w = window.open();
    if (w) {
        w.document.write(html);
        w.document.close();
    }
}

// Generate work ticket HTML (A4 landscape with column overflow like lms-svelte)
export function generateWorkTicketHTML(data: PrebookingData): string {
    // Parse return date like "Mon 16 Feb" into day and rest for the black box display
    const returnDateParts = data.returnDate.split(' ');
    const returnDateDay = returnDateParts.length >= 2 ? returnDateParts[1] : '';
    const returnDateRest = returnDateParts.length >= 3 ? `${returnDateParts[0]} ${returnDateParts[2]}` : data.returnDate;

    return `
<!DOCTYPE html>
<html>
<head>
    <title>Work Ticket - ${data.patientName}</title>
    <style>
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
        body { 
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            background: #fff;
            print-color-adjust: exact;
        }
        .page-container { 
            width: 100%; 
            margin: 0 auto; 
            overflow: visible; 
        }
        .content-columns { 
            column-count: 3; 
            column-gap: 20px; 
            column-fill: auto; 
            height: 100%; 
        }
        .content-section { 
            margin-bottom: 15px; 
            break-inside: avoid;
        }
        .info-label {
            font-size: 12px;
            color: #666;
            margin-bottom: 2px;
        }
        .info-value {
            font-size: 30px;
            font-weight: bold;
            line-height: 1.1;
            margin-bottom: 10px;
        }
        .info-value.medium {
            font-size: 24px;
        }
        .info-value.small {
            font-size: 18px;
        }
        .return-date-box { 
            background: #000; 
            color: #fff; 
            padding: 8px; 
            text-align: center; 
            width: 94px;
            float: right;
            margin-left: 10px;
        }
        .return-date-day { 
            font-size: 54px; 
            font-weight: bold; 
            line-height: 1; 
            margin-bottom: 6px; 
        }
        .return-date-rest { 
            font-size: 16px; 
            line-height: 1.2; 
        }
        .prebooking-badge {
            background: #376E97;
            color: #fff;
            padding: 6px 16px;
            font-size: 16px;
            font-weight: bold;
            display: inline-block;
            margin-bottom: 15px;
        }
        .details-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 10px 0; 
        }
        .details-table th { 
            border-bottom: 2px solid #000; 
            padding: 6px 8px; 
            text-align: left; 
            font-weight: bold; 
            font-size: 14px; 
            white-space: nowrap; 
        }
        .details-table td { 
            padding: 6px 8px; 
            text-align: left; 
            font-size: 14px; 
            border-bottom: 1px solid #e0e0e0; 
        }
        .comments-box h3 { 
            margin-bottom: 0; 
            font-size: 14px;
        }
        .comments-content { 
            white-space: pre-line; 
            border-top: 2px solid #000; 
            padding-top: 10px; 
            font-weight: bold; 
            font-size: 14px;
        }
        @page { margin: 0.5cm; size: A4 landscape; }
    </style>
</head>
<body>
    <div class="page-container">
        <div class="content-columns">
            <div class="content-section">
                <div class="prebooking-badge" style="width: 100%; text-align: center; margin-bottom: 0;">PREBOOKING</div>
            </div>

            <div class="content-section">
                <div class="return-date-box">
                    <div class="return-date-day">${returnDateDay}</div>
                    <div class="return-date-rest">${returnDateRest}</div>
                </div>
                <div class="info-label">Dentist & Practice Name</div>
                <div class="info-value medium">${data.dentistAndPractice}</div>
            </div>

            <div class="content-section">
                <div class="info-label">Patient</div>
                <div class="info-value medium">${data.patientName}</div>
            </div>

            <div class="content-section">
                <div class="info-label">Send Date</div>
                <div class="info-value small">${data.sendDate}</div>
            </div>

            ${data.referenceId ? `
            <div class="content-section">
                <div class="info-label">Reference</div>
                <div style="font-size: 14px; font-weight: bold;">${data.referenceId}</div>
            </div>
            ` : ''}

            <div class="content-section">
                <table class="details-table">
                    <thead>
                        <tr>
                            <th>Teeth</th>
                            <th>Item</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>${data.arch && data.arch !== '-' ? data.arch + ': ' : ''}${data.teeth || '-'}</td>
                            <td>${data.item}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            ${data.comments ? `
            <div class="content-section comments-box">
                <h3>Comments</h3>
                <div class="comments-content">${data.comments}</div>
            </div>
            ` : ''}
        </div>
    </div>
</body>
</html>
`;
}

// Generate label HTML (100mm x 62mm like lms-svelte address labels)
export function generateLabelHTML(data: PrebookingData): string {
    return `
<!DOCTYPE html>
<html>
<head>
    <style>
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        body { margin: 0; padding: 0px 10px; font-family: monospace; max-width: 96mm; }
        .header { 
            display: flex; 
            align-items: stretch; 
            margin-bottom: 2px; 
            height: 25px; 
        }
        .patient-name-box { 
            background: #000; 
            color: #fff; 
            line-height: 25px; 
            display: flex; 
            align-items: flex-start; 
            font-size: 15px; 
            font-weight: bold; 
            flex: 1; 
            white-space: nowrap; 
            overflow: hidden; 
            text-overflow: ellipsis; 
            padding-left: 4px;
        }
        .container { 
            display: flex; 
            width: 100%; 
            align-items: start; 
            font-size: 16px; 
            margin-bottom: 5px; 
        }
        .left { 
            text-align: left; 
            word-break: normal; 
            white-space: pre-line; 
            line-height: 1.2; 
            flex: 1; 
        }
        .center-column { 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            flex: 1; 
        }
        .day-date { 
            text-align: center; 
            white-space: nowrap; 
            font-weight: bold; 
        }
        .right { 
            text-align: right; 
            white-space: nowrap; 
            flex: 1; 
        }
    </style>
</head>
<body style="height: 100vh; box-sizing: border-box; display: flex; flex-direction: column;">
    <div class="header">
        <div class="patient-name-box">${data.patientName}</div>
    </div>
    <div class="container">
        <div class="left">${data.item}</div>
        <div class="center-column">
            <div class="day-date">${data.sendDate}</div>
        </div>
        <div class="right">Prebooking</div>
    </div>
    <div style="flex-grow: 1;"></div>
</body>
</html>
`;
}

// Print work ticket (A4 landscape: 297mm x 210mm)
export async function printWorkTicket(data: PrebookingData): Promise<void> {
    const html = generateWorkTicketHTML(data);
    const pdf = await generatePDF(html, { width: '297mm', height: '210mm' });
    const filename = `PREBOOKING ${data.patientName} (a4).pdf`;
    downloadPDF(pdf, filename);
}

// Print label (100mm x 62mm like lms-svelte address labels)
export async function printLabel(data: PrebookingData): Promise<void> {
    const html = generateLabelHTML(data);
    const pdf = await generatePDF(html, { width: '100mm', height: '61mm' });
    const filename = `PREBOOKING ${data.patientName} (62x100).pdf`;
    downloadPDF(pdf, filename);
}
